import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, MoreVertical, GripVertical, Palette, Tag, CheckCircle, XCircle } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import config from '../../config';
const API = `${config.BACKEND_URL}/api`;

const COLORS = [
  { value: 'bg-blue-500', label: 'Blue', preview: 'bg-blue-500' },
  { value: 'bg-green-500', label: 'Green', preview: 'bg-green-500' },
  { value: 'bg-yellow-500', label: 'Yellow', preview: 'bg-yellow-500' },
  { value: 'bg-orange-500', label: 'Orange', preview: 'bg-orange-500' },
  { value: 'bg-red-500', label: 'Red', preview: 'bg-red-500' },
  { value: 'bg-purple-500', label: 'Purple', preview: 'bg-purple-500' },
  { value: 'bg-pink-500', label: 'Pink', preview: 'bg-pink-500' },
  { value: 'bg-indigo-500', label: 'Indigo', preview: 'bg-indigo-500' },
  { value: 'bg-teal-500', label: 'Teal', preview: 'bg-teal-500' },
  { value: 'bg-gray-500', label: 'Gray', preview: 'bg-gray-500' },
];

const SortableStatusRow = ({ status, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: status.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-4 p-4 bg-card rounded-lg border"
      data-testid={`status-row-${status.id}`}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
      >
        <GripVertical className="h-5 w-5" />
      </div>
      
      <div className={`w-4 h-4 rounded-full ${status.color}`} />
      
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium">{status.name}</span>
          {status.is_default && (
            <Badge variant="secondary" className="text-xs">Default</Badge>
          )}
          {status.is_done && (
            <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">
              <CheckCircle className="h-3 w-3 mr-1" />
              Done
            </Badge>
          )}
          {status.is_rejected && (
            <Badge variant="secondary" className="text-xs bg-red-100 text-red-800">
              <XCircle className="h-3 w-3 mr-1" />
              Rejected
            </Badge>
          )}
        </div>
        <span className="text-sm text-muted-foreground font-mono">{status.key}</span>
      </div>
      
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" data-testid={`status-menu-${status.id}`}>
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onEdit(status)}>Edit</DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => onDelete(status)} 
            className="text-destructive"
            disabled={status.is_default}
          >
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export const AdminStatuses = () => {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [form, setForm] = useState({
    name: '',
    key: '',
    color: 'bg-gray-500',
    is_done: false,
    is_rejected: false
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchStatuses();
  }, []);

  const fetchStatuses = async () => {
    try {
      const response = await axios.get(`${API}/statuses`);
      setStatuses(response.data);
    } catch (error) {
      console.error('Failed to fetch statuses:', error);
      toast.error('Failed to load statuses');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingStatus) {
        await axios.put(`${API}/statuses/${editingStatus.id}`, form);
        toast.success('Status updated');
      } else {
        const maxOrder = statuses.length > 0 ? Math.max(...statuses.map(s => s.order)) + 1 : 1;
        await axios.post(`${API}/statuses`, { ...form, order: maxOrder });
        toast.success('Status created');
      }
      setDialogOpen(false);
      resetForm();
      fetchStatuses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save status');
    }
  };

  const handleEdit = (status) => {
    setEditingStatus(status);
    setForm({
      name: status.name,
      key: status.key,
      color: status.color,
      is_done: status.is_done || false,
      is_rejected: status.is_rejected || false
    });
    setDialogOpen(true);
  };

  const handleDelete = async (status) => {
    if (status.is_default) {
      toast.error('Cannot delete default statuses');
      return;
    }
    if (!window.confirm(`Delete status "${status.name}"?`)) return;
    
    try {
      await axios.delete(`${API}/statuses/${status.id}`);
      toast.success('Status deleted');
      fetchStatuses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete status');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;

    const oldIndex = statuses.findIndex(s => s.id === active.id);
    const newIndex = statuses.findIndex(s => s.id === over.id);
    
    const reorderedStatuses = arrayMove(statuses, oldIndex, newIndex);
    setStatuses(reorderedStatuses);

    try {
      await axios.put(`${API}/statuses/reorder`, { status_ids: reorderedStatuses.map(s => s.id) });
      toast.success('Order saved');
    } catch (error) {
      toast.error('Failed to save order');
      fetchStatuses();
    }
  };

  const resetForm = () => {
    setEditingStatus(null);
    setForm({ name: '', key: '', color: 'bg-gray-500', is_done: false, is_rejected: false });
  };

  const handleDialogClose = (open) => {
    if (!open) resetForm();
    setDialogOpen(open);
  };

  // Auto-generate key from name
  const handleNameChange = (name) => {
    setForm(prev => ({
      ...prev,
      name,
      key: editingStatus ? prev.key : name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
    }));
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner h-8 w-8" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-statuses-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold">User Story Statuses</h1>
          <p className="text-muted-foreground mt-1">Manage workflow statuses for user stories</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="create-status-btn">
              <Plus className="h-4 w-4" />
              New Status
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingStatus ? 'Edit Status' : 'Create Status'}</DialogTitle>
              <DialogDescription>
                {editingStatus ? 'Update status details' : 'Add a new workflow status'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="e.g., In Review"
                  required
                  data-testid="status-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="key">Key *</Label>
                <Input
                  id="key"
                  value={form.key}
                  onChange={(e) => setForm({ ...form, key: e.target.value })}
                  placeholder="e.g., in-review"
                  required
                  pattern="[a-z0-9-]+"
                  title="Lowercase letters, numbers, and hyphens only"
                  data-testid="status-key-input"
                />
                <p className="text-xs text-muted-foreground">Lowercase letters, numbers, and hyphens only</p>
              </div>
              <div className="space-y-2">
                <Label>Color</Label>
                <Select value={form.color} onValueChange={(v) => setForm({ ...form, color: v })}>
                  <SelectTrigger data-testid="status-color-select">
                    <SelectValue>
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded-full ${form.color}`} />
                        {COLORS.find(c => c.value === form.color)?.label || 'Select color'}
                      </div>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {COLORS.map((color) => (
                      <SelectItem key={color.value} value={color.value}>
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded-full ${color.preview}`} />
                          {color.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <Checkbox
                  id="is_done"
                  checked={form.is_done}
                  onCheckedChange={(checked) => setForm({ ...form, is_done: checked })}
                  data-testid="status-is-done-checkbox"
                />
                <div className="flex-1">
                  <Label htmlFor="is_done" className="text-sm font-medium cursor-pointer">
                    Mark as "Done" status
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Stories with this status are considered finished/completed
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <Checkbox
                  id="is_rejected"
                  checked={form.is_rejected}
                  onCheckedChange={(checked) => setForm({ ...form, is_rejected: checked })}
                  data-testid="status-is-rejected-checkbox"
                />
                <div className="flex-1">
                  <Label htmlFor="is_rejected" className="text-sm font-medium cursor-pointer">
                    Mark as "Rejected" status
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Offered when closing a story as rejected
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => handleDialogClose(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="status-submit-btn">
                  {editingStatus ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Workflow Statuses ({statuses.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Drag to reorder. The order determines column positions in the Kanban board.
          </p>
          
          {statuses.length === 0 ? (
            <div className="text-center py-8">
              <Palette className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No statuses configured</p>
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={statuses.map(s => s.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {statuses.map((status) => (
                    <SortableStatusRow
                      key={status.id}
                      status={status}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
